// src/redux/notificationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const NOTIF_BASE = `${process.env.REACT_APP_API_URL}/api/notifications`;

// Helper to get cookie
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
  return null;
};

// Helper for fetch with auth
const fetchWithAuth = async (url, options = {}, getState) => {
  const token = getState?.()?.auth?.user?.token || localStorage.getItem('token');
  const csrfToken = getCookie('XSRF-TOKEN');
  
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(csrfToken && { 'X-XSRF-TOKEN': csrfToken }),
      ...options.headers,
    },
  });
  
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Request failed');
  return data;
};

// Fetch notifications
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ limit = 20, offset = 0, unreadOnly = false } = {}, { getState, rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit, offset, unreadOnly });
      const data = await fetchWithAuth(`${NOTIF_BASE}?${params}`, {}, getState);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch notifications');
    }
  }
);

// Fetch unread count only
export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { getState, rejectWithValue }) => {
    try {
      const data = await fetchWithAuth(`${NOTIF_BASE}/unread-count`, {}, getState);
      return data.count;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch count');
    }
  }
);

// Mark single notification as read
export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId, { getState, rejectWithValue }) => {
    try {
      const data = await fetchWithAuth(`${NOTIF_BASE}/${notificationId}/read`, { method: 'POST' }, getState);
      return { notificationId, notification: data.notification };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to mark as read');
    }
  }
);

// Mark all notifications as read
export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { getState, rejectWithValue }) => {
    try {
      await fetchWithAuth(`${NOTIF_BASE}/mark-all-read`, { method: 'POST' }, getState);
      return true;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to mark all as read');
    }
  }
);

// Delete a notification
export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (notificationId, { getState, rejectWithValue }) => {
    try {
      await fetchWithAuth(`${NOTIF_BASE}/${notificationId}`, { method: 'DELETE' }, getState);
      return notificationId;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete');
    }
  }
);

// Clear all notifications
export const clearAllNotifications = createAsyncThunk(
  'notifications/clearAll',
  async (_, { getState, rejectWithValue }) => {
    try {
      await fetchWithAuth(`${NOTIF_BASE}/clear-all`, { method: 'DELETE' }, getState);
      return true;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to clear');
    }
  }
);

// Create test notification (for debugging)
export const createTestNotification = createAsyncThunk(
  'notifications/createTest',
  async (_, { getState, rejectWithValue, dispatch }) => {
    try {
      const data = await fetchWithAuth(`${NOTIF_BASE}/test`, { method: 'POST' }, getState);
      // Refetch notifications after creating
      dispatch(fetchNotifications());
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create test notification');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState: {
    items: [],
    unreadCount: 0,
    status: 'idle',
    error: null,
    hasMore: true,
    lastFetched: null,
  },
  reducers: {
    resetNotifications: (state) => {
      state.items = [];
      state.unreadCount = 0;
      state.status = 'idle';
      state.error = null;
      state.hasMore = true;
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.notifications;
        state.unreadCount = action.payload.unreadCount;
        state.hasMore = action.payload.hasMore;
        state.lastFetched = Date.now();
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      // Fetch unread count
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      
      // Mark as read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        const notification = state.items.find(n => n._id === notificationId);
        if (notification && !notification.read) {
          notification.read = true;
          notification.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      
      // Mark all as read
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.items.forEach(n => {
          n.read = true;
          n.readAt = new Date().toISOString();
        });
        state.unreadCount = 0;
      })
      
      // Delete notification
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const index = state.items.findIndex(n => n._id === action.payload);
        if (index !== -1) {
          if (!state.items[index].read) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
          state.items.splice(index, 1);
        }
      })
      
      // Clear all
      .addCase(clearAllNotifications.fulfilled, (state) => {
        state.items = [];
        state.unreadCount = 0;
      });
  },
});

export const { resetNotifications } = notificationSlice.actions;
export default notificationSlice.reducer;
